import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { memoNamed } from '../utils/react/memo-named';
import { Check, User } from '../types/user';
import {
  MyInfoWrapper,
  MyInfoUpper,
  MyInfoInput,
  PostNumberInput,
  Message,
  MessageSelect,
  Select,
  editedSelectColor,
  editedInputColor,
} from './common/styled';

interface Props {
  addressList: {
    tagKey: keyof User;
    tagName: string;
    privateIsDisabled: boolean;
    inputValue: string;
    isPublic: boolean;
    check: (input: string) => Check;
  }[];
  onUserInfoChange: (tagKey: string, value: string) => void;
}

const MessagePostNumber = styled.div`
  color: ${editedInputColor};
  display: inline-block;
`;

const MessageAddress = styled.div`
  color: ${editedInputColor};
  display: inline-block;
`;

export const MyPageAddress = memoNamed<Props>(
  'MyPageAddress',
  (props: Props) => {
    //postNumber
    const [postNumberInputValue, setPostNumberInputValue] = useState<string>(
      props.addressList[0]?.inputValue ?? ''
    );
    const [
      postNumberInputValuePrevious,
      setPostNumberInputValuePrevious,
    ] = useState<string>(props.addressList[0]?.inputValue);
    const [postNumberEdited, setPostNumberEdited] = useState<boolean>(false);
    const [postNumberMessage, setPostNumberMessage] = useState<string>('');
    const postNumberIsCorrect = props.addressList[0]?.check(
      postNumberInputValue
    ).isCorrect;
    const postNumberStyle = postNumberEdited
      ? {
          color: editedInputColor,
          borderColor: editedInputColor,
        }
      : {};

    const onPostNumberInputValueUpdate = useCallback(() => {
      // if unchanged
      if (postNumberInputValuePrevious === postNumberInputValue) return;
      // if not correct
      if (!postNumberIsCorrect) return;
      props.onUserInfoChange(
        props.addressList[0]?.tagKey,
        postNumberInputValue
      );

      // auto complete address

      const db = firebase.firestore();
      const user = firebase.auth().currentUser;
      setIsUpdating(true);
      setPostNumberEdited(false);
      db.collection('Users')
        .where('userID', '==', user?.uid)
        .get()
        .then((snapshot) => {
          snapshot.forEach((doc) => {
            const id = doc.id;
            const userInfo = props.addressList[0]?.privateIsDisabled
              ? { [props.addressList[0]?.tagKey]: postNumberInputValue }
              : {
                  [`${props.addressList[0]?.tagKey}.value`]: postNumberInputValue,
                };
            db.collection('Users')
              .doc(id)
              .update(userInfo)
              .then(() => {
                setPostNumberEdited(true);
                setPostNumberInputValuePrevious(postNumberInputValue);
                setIsUpdating(false);
                setPostNumberMessage('');
              })
              .catch((error) => {
                console.error(error);
                setIsUpdating(false);
                setPostNumberMessage('データの保存に失敗しました。');
              });
          });
        })
        .catch((err) => {
          console.error(err);
          setIsUpdating(false);
          setPostNumberMessage('データの保存に失敗しました。');
        });
    }, [
      props,
      postNumberInputValuePrevious,
      postNumberInputValue,
      postNumberIsCorrect,
    ]);

    const onPostNumberInputValueChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setPostNumberInputValue(event.target.value);
      },
      []
    );

    useEffect(() => {
      setPostNumberMessage(
        props.addressList[0]?.check(postNumberInputValue).alert
      );
    }, [postNumberInputValue, props]);

    //address
    const [addressInputValue, setAddressInputValue] = useState<string>(
      props.addressList[1]?.inputValue ?? ''
    );
    const [addressInputValuePrevious, setAddressInputValuePrevious] = useState<
      string
    >(props.addressList[1]?.inputValue);
    const [addressEdited, setAddressEdited] = useState<boolean>(false);
    const [addressMessage, setAddressMessage] = useState<string>('');
    const addressIsCorrect = props.addressList[1]?.check(addressInputValue)
      .isCorrect;
    const addressStyle = addressEdited
      ? {
          color: editedInputColor,
          borderColor: editedInputColor,
        }
      : {};

    const onAddressInputValueUpdate = useCallback(() => {
      // if unchanged
      if (addressInputValuePrevious === addressInputValue) return;
      // if not correct
      if (!addressIsCorrect) return;
      props.onUserInfoChange(props.addressList[1]?.tagKey, addressInputValue);
      const db = firebase.firestore();
      const user = firebase.auth().currentUser;
      setIsUpdating(true);
      setAddressEdited(false);
      db.collection('Users')
        .where('userID', '==', user?.uid)
        .get()
        .then((snapshot) => {
          snapshot.forEach((doc) => {
            const id = doc.id;
            const userInfo = props.addressList[1]?.privateIsDisabled
              ? { [props.addressList[1]?.tagKey]: addressInputValue }
              : {
                  [`${props.addressList[1]?.tagKey}.value`]: addressInputValue,
                };
            db.collection('Users')
              .doc(id)
              .update(userInfo)
              .then(() => {
                setAddressEdited(true);
                setAddressInputValuePrevious(addressInputValue);
                setIsUpdating(false);
                setAddressMessage('');
              })
              .catch((error) => {
                console.error(error);
                setIsUpdating(false);
                setAddressMessage('データの保存に失敗しました。');
              });
          });
        })
        .catch((err) => {
          console.error(err);
          setIsUpdating(false);
          setAddressMessage('データの保存に失敗しました。');
        });
    }, [props, addressInputValuePrevious, addressInputValue, addressIsCorrect]);

    useEffect(() => {
      setAddressMessage(props.addressList[1]?.check(addressInputValue).alert);
    }, [addressInputValue, props]);

    const onAddressInputValueChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setAddressInputValue(event.target.value);
      },
      []
    );

    //other
    const [isPublic, setIsPublic] = useState<boolean>(
      props.addressList[0]?.isPublic && props.addressList[1]?.isPublic
    );
    const [isPublicMessage, setIsPublicMessage] = useState<string>('');
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [editedSelect, setEditedSelect] = useState<boolean>(false);

    const selectStyle = editedSelect
      ? { color: editedSelectColor, borderColor: editedSelectColor }
      : {};

    const onIsPublicChange = useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        const _isPublic = event.target.value === 'public';
        setIsPublic(_isPublic);
        setEditedSelect(false);
        const db = firebase.firestore();
        const user = firebase.auth().currentUser;
        setIsUpdating(true);
        db.collection('Users')
          .where('userID', '==', user?.uid)
          .get()
          .then((snapshot) => {
            snapshot.forEach((doc) => {
              const id = doc.id;
              db.collection('Users')
                .doc(id)
                .update({
                  [`${props.addressList[0]?.tagKey}.open`]: _isPublic,
                  [`${props.addressList[1]?.tagKey}.open`]: _isPublic,
                })
                .then(() => {
                  setIsUpdating(false);
                  setIsPublicMessage('');
                  setEditedSelect(true);
                });
            });
          })
          .catch((err) => {
            console.error(err);
            setIsUpdating(false);
            setIsPublicMessage('データの保存に失敗しました。');
          });
      },
      [props]
    );

    useEffect(() => {
      setPostNumberInputValue(props.addressList[0]?.inputValue ?? '');
      setPostNumberInputValuePrevious(props.addressList[0]?.inputValue ?? '');
      setAddressInputValue(props.addressList[1]?.inputValue ?? '');
      setAddressInputValuePrevious(props.addressList[1]?.inputValue ?? '');
    }, [props]);

    return (
      <div>
        <MyInfoWrapper>
          <MyInfoUpper>
            {props.addressList[1]?.tagName}
            {addressInputValue === '' ? undefined : (
              <Select
                style={selectStyle}
                value={isPublic ? 'public' : 'private'}
                onChange={onIsPublicChange}
              >
                <option value='public'>名簿に記載</option>
                <option value='private'>非公開</option>
              </Select>
            )}
            {isUpdating ? '  通信中...' : undefined}
            {editedSelect && addressInputValue !== '' ? (
              <MessageSelect>【保存済】</MessageSelect>
            ) : undefined}
            {postNumberEdited ? (
              <MessagePostNumber>【保存済】</MessagePostNumber>
            ) : undefined}
            {addressEdited ? (
              <MessageAddress>【保存済】</MessageAddress>
            ) : undefined}
          </MyInfoUpper>
          <Message>{isPublicMessage}</Message>
          <Message>{postNumberMessage}</Message>
          郵便番号〒
          <PostNumberInput
            style={postNumberStyle}
            type='text'
            value={postNumberInputValue}
            onChange={onPostNumberInputValueChange}
            onBlur={onPostNumberInputValueUpdate}
          />
          <br />
          <Message>{addressMessage}</Message>
          <MyInfoInput
            style={addressStyle}
            type='text'
            value={addressInputValue}
            onChange={onAddressInputValueChange}
            onBlur={onAddressInputValueUpdate}
          />
        </MyInfoWrapper>
      </div>
    );
  }
);
